import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth, userMode } from "@/context/AuthContext";
import { useIntegrations, providerReady, isNotConfigured } from "@/context/IntegrationsContext";
import NavTop from "@/components/nav/nav_top";
import InputField from "@/components/forms/input_field";
import SelectField from "@/components/forms/select_field";
import TextareaField from "@/components/forms/textarea_field";
import ButtonMain from "@/components/buttons/button_main";
import ButtonGhost from "@/components/buttons/button_ghost";
import ConfirmDialog from "@/components/admin/confirm_dialog";
import PhotoUpload from "@/components/profile/photo_upload";
import BadgeVerified from "@/components/badges/badge_verified";
import VerificationCard from "@/components/profile/verification_card";
import ModeSwitchCard from "@/components/profile/mode_switch_card";
import ListerStatusCard from "@/components/profile/lister_status_card";
import ViewingsCard from "@/components/viewings/viewings_card";

const INCOME_RANGES = ["$20k to $40k", "$40k to $60k", "$60k to $80k", "$80k to $100k", "$100k to $140k", "$140k plus"];
const INCOME_OPTIONS = [{ value: "", label: "Select income range" }, ...INCOME_RANGES.map((v) => ({ value: v, label: v }))];
// The API wants lease_length_wanted as INTEGER months — option values are the
// numbers, labels stay human. Month to month is 1.
const LEASE_OPTIONS = [
  { value: "", label: "Select lease length" },
  { value: "6", label: "6 months" },
  { value: "8", label: "8 months" },
  { value: "12", label: "12 months" },
  { value: "18", label: "18 months" },
  { value: "24", label: "24 months" },
  { value: "1", label: "Month to month" },
];
// The API wants pets as one of none/cat/dog/both.
const PET_OPTIONS = [
  { value: "none", label: "No pets" },
  { value: "cat", label: "Cat" },
  { value: "dog", label: "Dog" },
  { value: "both", label: "Cat and dog" },
];
const PET_VALUES = new Set(PET_OPTIONS.map((o) => o.value));
// The API wants smoking as "yes"/"no".
const YN = [{ value: "no", label: "No" }, { value: "yes", label: "Yes" }];

// Normalize whatever the backend hands back into the form's option values.
function toLeaseValue(v) {
  if (v == null || v === "") return "";
  const n = parseInt(v, 10);
  if (Number.isFinite(n) && n > 0) return String(n);
  return /month to month/i.test(String(v)) ? "1" : "";
}
function toPetsValue(v) {
  if (PET_VALUES.has(v)) return v;
  if (v === "yes") return "both"; // legacy yes/no answers
  return "none";
}

const schema = z
  .object({
    photo: z.string().min(1, "Add a profile photo"),
    name: z.string().min(2, "Enter your full name"),
    phone: z.string().min(7, "Enter your phone number"),
    occupation: z.string().min(2, "Tell listers what you do"),
    income_range: z.string().min(1, "Select your income range"),
    budget_min: z.coerce.number({ invalid_type_error: "Enter a number" }).min(1, "Enter your minimum budget"),
    budget_max: z.coerce.number({ invalid_type_error: "Enter a number" }).min(1, "Enter your maximum budget"),
    move_in: z.string().min(1, "Pick your move in date"),
    lease_length_wanted: z.string().min(1, "Select a lease length"),
    household: z.string().optional(),
    pets: z.string().optional(),
    smoking: z.string().optional(),
    about: z.string().min(40, "Tell listers a little more. At least 40 characters."),
    references: z
      .array(
        z.object({
          name: z.string().min(2, "Name is required"),
          relation: z.string().min(2, "Relation is required"),
          phone: z.string().optional().or(z.literal("")),
          email: z.string().email("Enter a valid email").optional().or(z.literal("")),
        })
      )
      .max(5, "Up to 5 references"),
  })
  .refine((d) => Number(d.budget_max) >= Number(d.budget_min), {
    message: "Max budget must be at least the min budget",
    path: ["budget_max"],
  });

export default function ProfileRenterPage() {
  const { user, setUser, refresh } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // This page collects the RENTER profile (budget, income, pets, references).
  // A user acting as a lister/realtor must never be asked these — send them to
  // their lister profile instead. Keyed on active mode so a dual-mode account
  // in renter mode still sees this page; realtors (active_mode lister_realtor)
  // resolve to "lister" and are redirected.
  const isListerMode = user && userMode(user) === "lister";
  useEffect(() => {
    if (isListerMode) {
      navigate("/profile/lister", { replace: true, state: location.state });
    }
  }, [isListerMode, navigate, location.state]);
  // The banner clears the moment the profile is complete, even when the page
  // was opened with the welcome flag from signup. A first Google sign in
  // arrives via a cross-site redirect that cannot carry router state, so the
  // backend appends ?welcome=1 instead — treat it the same as state.welcome.
  const welcomeFlag =
    !!location.state?.welcome || new URLSearchParams(location.search).get("welcome") === "1";
  const showWelcome = user?.profile_complete === false || (welcomeFlag && user?.profile_complete !== true);

  const defaults = useMemo(
    () => ({
      photo: user?.photo_url || "",
      name: user?.name || "",
      phone: user?.phone || "",
      occupation: "",
      income_range: "",
      budget_min: "",
      budget_max: "",
      move_in: "",
      lease_length_wanted: "",
      household: "Single",
      pets: "none",
      smoking: "no",
      about: "",
      references: [],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    getValues,
    reset,
    formState: { errors, isValid, isSubmitting },
  } = useForm({ resolver: zodResolver(schema), defaultValues: defaults, mode: "onChange" });

  const { fields, append, remove } = useFieldArray({ control, name: "references" });
  const about = watch("about") || "";

  // AI "about me" suggestions. The button only renders while OpenAI is
  // configured (providerReady); a 503 from the API additionally hides it
  // quietly with one info toast, in case the status call was stale.
  const { status: integrations } = useIntegrations();
  const [aiHidden, setAiHidden] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const aiUnavailableToastShown = useRef(false);
  const aiAvailable = providerReady(integrations, "openai") && !aiHidden;

  async function suggestAbout() {
    if (aiLoading) return;
    const v = getValues();
    setAiLoading(true);
    try {
      const { data } = await api.post("/renters/me/about-suggestions", {
        occupation: v.occupation || null,
        income_range: v.income_range || null,
        budget_min: v.budget_min ? Number(v.budget_min) : null,
        budget_max: v.budget_max ? Number(v.budget_max) : null,
        move_in: v.move_in || null,
        lease_length_wanted: v.lease_length_wanted ? parseInt(v.lease_length_wanted, 10) : null,
        household: v.household || null,
        pets: v.pets || null,
        smoking: v.smoking || null,
      });
      const list = Array.isArray(data?.suggestions) ? data.suggestions.filter(Boolean) : [];
      if (list.length === 0) {
        toast.warning("No suggestions came back. Please try again.");
      }
      setAiSuggestions(list);
    } catch (e) {
      if (isNotConfigured(e)) {
        // OpenAI is not connected: hide the button quietly, tell them once.
        setAiHidden(true);
        if (!aiUnavailableToastShown.current) {
          aiUnavailableToastShown.current = true;
          toast.info("AI suggestions are not available yet.");
        }
      } else if (e?.response?.status === 429) {
        toast.warning("You have used up your suggestions for now. Try again in a little while.");
      } else {
        toast.warning("We could not fetch suggestions right now. Please try again.");
      }
    } finally {
      setAiLoading(false);
    }
  }

  // Pre-fill from the saved renter profile when the backend has one.
  // Single flight: the guard ref stops StrictMode/re-render double fetches,
  // and an empty or 404 profile is expected — it renders a blank form with
  // no toast. Errors only ever surface on save.
  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    let alive = true;
    api
      .get("/renters/me")
      .then((res) => {
        if (!alive || !res.data || typeof res.data !== "object") return;
        // GET /api/renters/me wraps the fields: { profile: { ... } }. Older
        // responses were flat, so fall back to the body itself.
        const p =
          res.data.profile && typeof res.data.profile === "object" ? res.data.profile : res.data;
        reset({
          photo:
            user?.photo_url ||
            p.photo ||
            p.photo_url ||
            (Array.isArray(p.photos) ? p.photos[0] : "") ||
            "",
          // The display name lives on the auth user, not the renter profile.
          name: user?.name || "",
          // Profile phone first — it is what the renter actually submitted.
          phone: p.phone || user?.phone || "",
          occupation: p.occupation || "",
          income_range: p.income_range || "",
          budget_min: p.budget_min ?? "",
          budget_max: p.budget_max ?? "",
          // The date input needs plain YYYY-MM-DD even if the API sends a
          // full ISO datetime.
          move_in: p.move_in ? String(p.move_in).slice(0, 10) : "",
          // Select values are strings — lease comes back as an integer.
          lease_length_wanted: toLeaseValue(p.lease_length_wanted),
          household: p.household || "Single",
          pets: toPetsValue(p.pets),
          // The API stores smoking as a boolean; legacy rows may hold yes/no.
          smoking:
            p.smoking === true || p.smoking === "yes"
              ? "yes"
              : p.smoking === false || p.smoking === "no"
              ? "no"
              : "no",
          about: p.about || p.about_me || p.bio || "",
          references: Array.isArray(p.references)
            ? p.references.slice(0, 5).map((r) => ({
                name: r?.name || "",
                relation: r?.relation || "",
                phone: r?.phone || "",
                email: r?.email || "",
              }))
            : [],
        });
      })
      .catch(() => {
        // No saved profile yet, or backend offline — the blank form is fine.
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The saved photo lives on the auth user (photo_url). Auth can resolve
  // after this form mounts, so sync it in whenever it appears — this is what
  // makes the photo stick across reloads.
  const photoValue = watch("photo");
  useEffect(() => {
    if (user?.photo_url && !photoValue) {
      setValue("photo", user.photo_url, { shouldValidate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.photo_url]);

  // Same late-auth sync for the name: Google signups may carry an email
  // derived placeholder, and /auth/me can resolve after this form mounts.
  const nameValue = watch("name");
  useEffect(() => {
    if (user?.name && !nameValue) {
      setValue("name", user.name, { shouldValidate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.name]);

  // Same late-auth sync for the phone: the profile fetch can finish before
  // /auth/me resolves, so backfill the account phone into an empty field.
  const phoneValue = watch("phone");
  useEffect(() => {
    if (user?.phone && !phoneValue) {
      setValue("phone", user.phone, { shouldValidate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.phone]);

  async function onSubmit(values) {
    const payload = {
      occupation: values.occupation,
      income_range: values.income_range,
      budget_min: Number(values.budget_min),
      budget_max: Number(values.budget_max),
      move_in: values.move_in,
      // Integer months — "12", never "12 months".
      lease_length_wanted: parseInt(values.lease_length_wanted, 10),
      household: values.household,
      pets: values.pets || "none",
      smoking: values.smoking === "yes" ? "yes" : "no",
      about: values.about,
      references: (values.references || []).slice(0, 5),
      phone: values.phone,
    };
    try {
      // The display name lives on the user document, not the renter profile.
      // Google signups can arrive with an email derived placeholder name, so
      // persist any change through the account endpoint first.
      if (values.name && values.name.trim() !== (user?.name || "")) {
        await api.put("/users/me", { name: values.name.trim() });
      }
      await api.put("/renters/me", payload);
      // Re-fetch /auth/me so profile_complete, missing_fields and photo_url
      // all reflect the save immediately (this also clears the banner). The
      // save DID succeed, so if the refresh itself fails or comes back stale,
      // mark the session complete locally — otherwise the finish-your-profile
      // gate keeps reopening off the old user object.
      const fresh = await refresh();
      if (!fresh || fresh.profile_complete === undefined) {
        setUser((u) =>
          u ? { ...u, name: values.name, phone: values.phone, profile_complete: true, missing_fields: [] } : u
        );
      }
      toast.success("Profile saved. You are ready to swipe.");
    } catch (error) {
      if (error?.response) {
        const data = error.response.data;
        // 422 comes back as {"code":"VALIDATION","errors":[{field,message}]}
        // — pin each message onto its own field instead of a vague toast.
        if (error.response.status === 422 && data?.code === "VALIDATION" && Array.isArray(data.errors)) {
          data.errors.forEach((e, i) => {
            if (e?.field) {
              setError(e.field, { type: "server", message: e.message || "Check this field" }, { shouldFocus: i === 0 });
            }
          });
          toast.warning("Some fields need a second look. Check the highlighted ones.");
          return;
        }
        toast.warning("We could not save your profile. Please check the form and try again.");
        return;
      }
      // Backend offline — keep the demo session moving with a local update.
      toast.success("Profile saved. You are ready to swipe.");
      setUser((u) =>
        u
          ? { ...u, name: values.name, photo_url: values.photo, phone: values.phone, profile_complete: true, missing_fields: [] }
          : u
      );
    }
  }

  // Redirecting a lister/realtor away — render nothing so the renter form
  // never flashes before the navigation lands.
  if (isListerMode) return null;

  return (
    <div className="min-h-screen bg-slate-50/70 pb-24 md:pb-12">
      <NavTop />
      <main className="mx-auto max-w-3xl px-4 pt-6 sm:px-6">
        {showWelcome && (
          <div className="mb-4 flex items-start gap-3 rounded-2xl bg-brand-tint px-4 py-3">
            <Sparkles size={16} className="mt-0.5 shrink-0 text-brand-lime" />
            <p className="text-sm font-medium text-brand-ink">
              Complete your profile to start swiping. Every field below marked required is what listers need to say yes.
            </p>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2.5">
          <h1 className="text-2xl font-extrabold tracking-tight text-brand-ink">Your renter profile</h1>
          {(user?.verified || user?.kyc_status === "approved") && (
            <BadgeVerified label="Verified" className="border border-brand-line" />
          )}
        </div>
        <p className="mt-1 text-sm font-semibold text-slate-500">
          A complete profile gets three times more matches, {user?.name?.split(" ")[0] || "there"}.
        </p>

        <VerificationCard
          user={user}
          onStatusChange={(next) => setUser((u) => (u ? { ...u, kyc_status: next } : u))}
        />

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <section className="mt-6 rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
            <Controller
              control={control}
              name="photo"
              render={({ field }) => (
                <PhotoUpload value={field.value} onChange={field.onChange} error={errors.photo?.message} />
              )}
            />
          </section>

          <section className="mt-4 space-y-4 rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
            <h2 className="font-extrabold text-brand-ink">About you</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <InputField
                label="Full name"
                placeholder="Alex Morgan"
                error={errors.name?.message}
                {...register("name")}
              />
              <InputField
                label="Phone number"
                type="tel"
                placeholder="416 555 0139"
                error={errors.phone?.message}
                {...register("phone")}
              />
              <InputField
                label="Occupation"
                placeholder="Product designer"
                error={errors.occupation?.message}
                {...register("occupation")}
              />
              <SelectField
                label="Income range"
                options={INCOME_OPTIONS}
                error={errors.income_range?.message}
                {...register("income_range")}
              />
              <SelectField
                label="Lease length wanted"
                options={LEASE_OPTIONS}
                error={errors.lease_length_wanted?.message}
                {...register("lease_length_wanted")}
              />
              <InputField
                label="Budget min per month"
                type="number"
                min="0"
                placeholder="1500"
                error={errors.budget_min?.message}
                {...register("budget_min")}
              />
              <InputField
                label="Budget max per month"
                type="number"
                min="0"
                placeholder="2500"
                error={errors.budget_max?.message}
                {...register("budget_max")}
              />
              <InputField label="Move in date" type="date" error={errors.move_in?.message} {...register("move_in")} />
              <InputField label="Household" placeholder="Single, Couple, Family of three" {...register("household")} />
              <SelectField label="Pets" options={PET_OPTIONS} error={errors.pets?.message} {...register("pets")} />
              <SelectField label="Smoking" options={YN} error={errors.smoking?.message} {...register("smoking")} />
            </div>
            <div>
              <TextareaField
                label="About me"
                rows={4}
                placeholder="Tell listers who you are, how you live, and what you are looking for"
                error={errors.about?.message}
                {...register("about")}
              />
              <p className="mt-1 text-right text-xs font-medium text-slate-400">
                {about.length < 40 ? `${40 - about.length} more characters needed` : `${about.length} characters`}
              </p>
              {aiAvailable && (
                <div className="mt-1">
                  <ButtonGhost onClick={suggestAbout} disabled={aiLoading}>
                    <Sparkles size={15} /> {aiLoading ? "Writing suggestions" : "Suggest with AI"}
                  </ButtonGhost>
                  {aiSuggestions.length > 0 && (
                    <div className="mt-3 grid gap-2">
                      {aiSuggestions.map((s, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => {
                            setValue("about", s, { shouldValidate: true, shouldDirty: true });
                            setAiSuggestions([]);
                          }}
                          className="rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-left text-sm text-brand-ink transition hover:border-brand-primary hover:bg-brand-tint"
                        >
                          {s}
                        </button>
                      ))}
                      <p className="text-xs font-medium text-slate-400">
                        Tap a suggestion to use it. You can edit it before saving.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>

          <section className="mt-4 rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="font-extrabold text-brand-ink">
                References <span className="text-sm font-semibold text-slate-400">(optional, up to 5)</span>
              </h2>
              <ButtonGhost
                onClick={() => append({ name: "", relation: "", phone: "", email: "" })}
                disabled={fields.length >= 5}
              >
                <Plus size={15} /> Add reference
              </ButtonGhost>
            </div>
            <p className="mt-1 text-sm font-semibold text-slate-500">
              Past landlords or employers. They are only shared with listers you match with.
            </p>
            {fields.length === 0 && (
              <p className="mt-4 rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-400">
                No references yet. Adding one or two makes listers say yes faster.
              </p>
            )}
            <div className="mt-4 space-y-4">
              {fields.map((f, i) => (
                <div key={f.id} className="rounded-2xl border border-slate-100 bg-slate-50/60 p-4">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Reference {i + 1}</p>
                    <button
                      type="button"
                      aria-label={`Remove reference ${i + 1}`}
                      onClick={() => remove(i)}
                      className="rounded-full p-1.5 text-slate-400 transition hover:bg-white hover:text-amber-600"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                  <div className="mt-2 grid gap-3 sm:grid-cols-2">
                    <InputField
                      label="Name"
                      error={errors.references?.[i]?.name?.message}
                      {...register(`references.${i}.name`)}
                    />
                    <InputField
                      label="Relation"
                      placeholder="Previous landlord"
                      error={errors.references?.[i]?.relation?.message}
                      {...register(`references.${i}.relation`)}
                    />
                    <InputField
                      label="Phone"
                      type="tel"
                      error={errors.references?.[i]?.phone?.message}
                      {...register(`references.${i}.phone`)}
                    />
                    <InputField
                      label="Email"
                      type="email"
                      error={errors.references?.[i]?.email?.message}
                      {...register(`references.${i}.email`)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>

          <div className="mt-6">
            {/* Button stays clickable while incomplete: pressing it runs
                validation and flags every missing required field in place
                (red border + message) so the renter sees exactly what's left. */}
            <ButtonMain type="submit" full disabled={isSubmitting}>
              {isSubmitting ? "Saving" : "Save profile"}
            </ButtonMain>
            {!isValid && (
              <p className="mt-2 text-center text-xs font-medium text-red-500">
                Fill in the required fields highlighted above to save your profile.
              </p>
            )}
          </div>
        </form>

        <ViewingsCard className="mt-4" />
        <ListerStatusCard />
        <ModeSwitchCard />
        <AccountSection />
      </main>
    </div>
  );
}

// Account controls: deactivate (reversible, hidden until next login) and
// delete (permanent, requires typing DELETE). Amber only, never red.
function AccountSection() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  async function deactivate() {
    try {
      await api.post("/users/me/deactivate");
      toast.success("Your account is deactivated. Log in any time to reactivate.");
      await logout();
      navigate("/");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function deleteAccount() {
    setBusy(true);
    try {
      await api.delete("/users/me", { data: { confirm: "DELETE" } });
      toast.success("Your account has been deleted.");
      setDeleteOpen(false);
      await logout();
      navigate("/");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mt-4 rounded-3xl border border-brand-line bg-white p-6 shadow-hairline">
      <h2 className="font-extrabold text-brand-ink">Account</h2>
      <div className="mt-4 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-brand-ink">Deactivate account</p>
            <p className="text-xs text-brand-secondary">
              Take a break. Your profile and listings are hidden until you log in again and reactivate.
            </p>
          </div>
          <ButtonGhost onClick={() => setDeactivateOpen(true)}>Deactivate account</ButtonGhost>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-brand-line pt-4">
          <div>
            <p className="text-sm font-semibold text-brand-ink">Delete account</p>
            <p className="text-xs text-brand-secondary">Permanently remove your account and everything in it.</p>
          </div>
          <button
            type="button"
            onClick={() => {
              setConfirmText("");
              setDeleteOpen(true);
            }}
            className="text-sm font-semibold text-amber-600 transition hover:text-amber-700 hover:underline"
          >
            Delete account
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={deactivateOpen}
        title="Deactivate your account?"
        message="Your profile and listings are hidden while your account is deactivated. Log in again at any time to reactivate and pick up where you left off."
        confirmLabel="Deactivate"
        onClose={() => setDeactivateOpen(false)}
        onConfirm={deactivate}
      />

      {deleteOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-brand-ink/50 p-4 backdrop-blur-sm"
          onClick={() => setDeleteOpen(false)}
        >
          <div
            className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-extrabold text-brand-ink">Delete your account?</h3>
            <p className="mt-1 text-sm text-slate-500">
              This permanently removes your profile, listings, matches and messages. It cannot be undone. Type DELETE
              below to confirm.
            </p>
            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type DELETE"
              autoFocus
              className="mt-4 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
            />
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteOpen(false)}
                className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={confirmText !== "DELETE" || busy}
                onClick={deleteAccount}
                className="rounded-xl bg-amber-500 px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy ? "Deleting" : "Delete my account"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
